import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import classNames from 'classnames';
import Submenu from './submenu';
import UserContext from '../context/userContext';
import { EventBus as eventBus } from "../EventBus";
import { hasMenuPermission } from '../../utils/permissions';
import alertsService from '../../services/alerts';
import { getCookie } from '../../services/axiosInstance';
import './Menu.css';

// Modal de alertas críticos (só volta a aparecer 5 min após fechar)
const CRITICAL_ALERTS_MODAL_COOLDOWN_MS = 5 * 60 * 1000;
const CRITICAL_ALERTS_MODAL_CLOSED_KEY = 'criticalAlertsModalClosedAt';

const Menu = ({ loadFunction, ePOSOptions = { menu: true } }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [activeAlerts, setActiveAlerts] = useState([]);
  const [isAlertsDropdownOpen, setIsAlertsDropdownOpen] = useState(false);
  const [showCriticalModal, setShowCriticalModal] = useState(false);
  const [criticalAlerts, setCriticalAlerts] = useState([]);
  const context = React.useContext(UserContext);
  const location = useLocation();
  const alertsDropdownRef = useRef(null);

  useEffect(() => {
    eventBus.$emit("footer", { company: { name: "Walk In DEV", url: "https://www.walkindev.com/" } });

    // Add/remove body class when menu is open
    if (isMenuOpen) {
      document.body.classList.add('menu-open');
    } else {
      document.body.classList.remove('menu-open');
    }

    return () => {
      document.body.classList.remove('menu-open');
    };
  }, [isMenuOpen]);

  useEffect(() => {
    if(loadFunction && typeof loadFunction === 'function'){
        loadFunction();
    }
  }, [loadFunction])

  const showCriticalAlertsModal = useCallback((criticalAlerts) => {
    const closedAt = sessionStorage.getItem(CRITICAL_ALERTS_MODAL_CLOSED_KEY);
    if (closedAt && (Date.now() - parseInt(closedAt, 10)) < CRITICAL_ALERTS_MODAL_COOLDOWN_MS) {
      return;
    }
    setShowCriticalModal(true);
  }, []);

  const hideCriticalAlertsModal = useCallback(() => {
    setShowCriticalModal(false);
    sessionStorage.setItem(CRITICAL_ALERTS_MODAL_CLOSED_KEY, String(Date.now()));
  }, []);

  // Load alerts from API
  const loadAlerts = useCallback(async () => {
    const idempresa = getCookie('member_idempresa') || '205';
    try {
      const data = await alertsService.check(idempresa);
      setActiveAlerts(data);
      
      // Filtrar alertas críticos
      const critical = (data || []).filter(a => a.critical === true);
      if (critical.length > 0) {
        setCriticalAlerts(critical);
        showCriticalAlertsModal(critical);
      }
    } catch (error) {
      console.error('Erro ao carregar alertas:', error);
      setActiveAlerts([]);
    }
  }, [showCriticalAlertsModal]);

  // Load alerts on mount and whenever route changes
  useEffect(() => {
    loadAlerts();
  }, [location.pathname, loadAlerts]);

  // Refresh alerts every 1 minute
  useEffect(() => {
    const interval = setInterval(loadAlerts, 1 * 60 * 1000);
    return () => clearInterval(interval);
  }, [loadAlerts]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (alertsDropdownRef.current && !alertsDropdownRef.current.contains(event.target)) {
        setIsAlertsDropdownOpen(false);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, []);

  // Toggle alerts dropdown
  const handleAlertsClick = (e) => {
    e.preventDefault();
    if (activeAlerts.length === 0) {
      return;
    }
    setIsAlertsDropdownOpen(!isAlertsDropdownOpen);
  };

  const exitDemo = () => {
    if (window.location.hostname !== "localhost") {
      window.location.href = `https://react.eposgestofinal.com${window.location.pathname}`;
    } else {
      window.location.reload();
    }
  };

  if (!ePOSOptions.menu) {
    return null;
  }

  const handleMenuToggle = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const handleLinkClick = () => {
    setIsMenuOpen(false);
  };

  const submenucontabilidade = [
    {
      condition: hasMenuPermission('accounting:statistics', context),
      title: 'Estatísticas',
      url: 'https://eposgestofinal.com/index.php?page=statistics',
      icon: 'chart-line',
    },
    {
      condition: hasMenuPermission('accounting:consumption/vehicles', context),
      title: 'Consumos por matrícula',
      url: '/consumption/vehicles',
      icon: 'car',
    },
    {
      condition: hasMenuPermission('accounting:turno', context),
      title: 'Declaração de Caixa',
      url: 'https://eposgestofinal.com/index.php?page=turno',
      icon: 'cash-register',
    },
    {
      condition: hasMenuPermission('accounting:stocks', context),
      title: 'Existências em stock',
      url: '/stocks',
      icon: 'warehouse',
    },
    {
      condition: hasMenuPermission('accounting:estimates', context),
      title: 'Sondagens',
      url: '/estimates',
      icon: 'chart-bar',
    },
    {
      condition: hasMenuPermission('accounting:anmc', context),
      title: 'Listagem ENSE',
      url: 'https://eposgestofinal.com/index.php?page=anmc',
      icon: 'file-contract',
    },
    {
      condition: hasMenuPermission('accounting:saft', context),
      title: 'SAFT-PT v1.4',
      url: 'https://eposgestofinal.com/index.php?page=saft',
      icon: 'file-code',
    },
  ];

  const submenuToolsLojista = [
    {
      condition: hasMenuPermission('tools:agenda', context),
      title: 'Agenda',
      url: '/agenda',
      icon: 'calendar-alt',
    },
    {
      condition: hasMenuPermission('tools:calendar', context),
      title: 'Calendário',
      url: '/calendar',
      icon: 'calendar-day',
    },
  ];

  const submenuTools = [
    {
      condition: hasMenuPermission('tools', context),
      title: 'Gestão',
      disabled: true,
    },
    {
      condition: context.permissions?.includes("site.newclientes") ? hasMenuPermission('tools:clients', context) : hasMenuPermission('tools:customers', context),
      title: context.permissions?.includes("site.newclientes") ? 'Clientes (beta)' : 'Clientes',
      url: context.permissions?.includes("site.newclientes") ? '/clientes' : '/customers',
      icon: 'users',
    },
    {
      condition: hasMenuPermission('tools:providers', context),
      title: 'Fornecedores',
      url: '/providers',
      icon: 'truck',
    },
    {
      condition: hasMenuPermission('tools:partners', context),
      title: 'Parceiros',
      url: '/partners',
      icon: 'handshake',
    },
    {
      condition: hasMenuPermission('tools:products', context),
      title: 'Produtos',
      url: '/products',
      icon: 'box',
    },
    {
      condition: hasMenuPermission('tools', context),
      title: 'Utilitários',
      disabled: true,
    },
    {
      condition: hasMenuPermission('tools:agenda', context),
      title: 'Agenda',
      url: '/agenda',
      icon: 'calendar-alt',
    },
    {
      condition: hasMenuPermission('tools:calendar', context),
      title: 'Calendário',
      url: '/calendar',
      icon: 'calendar-day',
    },
    {
      condition: hasMenuPermission('tools:downloads', context),
      title: 'Downloads',
      url: 'https://eposgestofinal.com/index.php?page=downloads',
      icon: 'download',
    },
    {
      condition: hasMenuPermission('tools:services', context),
      title: 'Serviços',
      url: '/customers',
      icon: 'cogs',
    },
    {
      condition: hasMenuPermission('tools:tourniquet', context),
      title: 'Torniquete',
      url: '/tourniquet',
      icon: 'door-open',
    },
  ];

  const submenuDocuments = [
    {
      condition: hasMenuPermission('documents:orders', context),
      title: 'Compras',
      url: '/orders',
      icon: 'shopping-cart',
    },
    {
      condition: hasMenuPermission('documents:payments', context),
      title: 'Pagamentos',
      url: '/payments',
      icon: 'donate',
    },
    {
      condition: hasMenuPermission('documents:guias', context),
      title: 'Guias de Transporte TPV',
      url: '/guias',
      icon: 'file-alt',
    },
    {
      condition: true,
      title: hasMenuPermission('documents:sales', context) ? 'Faturação' : 'Faturação (Brevemente)',
      url: hasMenuPermission('documents:sales', context) ? '/sales' : '#',
      icon: 'file-invoice-dollar',
    },
    {
      condition: hasMenuPermission('documents:tickets', context),
      title: 'Senhas',
      url: '/tickets',
      icon: 'ticket-alt',
    },
    {
      condition: hasMenuPermission('documents:cc', context),
      title: 'Documentos Pendentes',
      url: '/cc',
      icon: 'clock',
    },
  ];

  const adminTools = [
    {
      condition: hasMenuPermission('admin:users_management', context),
      title: 'Utilizadores',
      url: 'https://eposgestofinal.com/index.php?page=users_management',
      icon: 'users-cog',
    },
    {
      condition: hasMenuPermission('admin:menu_admin', context),
      title: 'Permissões',
      url: '/menu-permissions',
      icon: 'lock-open',
    },
    {
      condition: hasMenuPermission('admin:site_admin', context),
      title: 'Site',
      url: 'https://eposgestofinal.com/index.php?page=site_admin',
      icon: 'globe',
    },
    {
      condition: hasMenuPermission('admin:anulados', context),
      title: 'Anulação de Talões',
      url: '/anulados',
      icon: 'file-excel',
    }
  ];

  const configTools = [
    {
      condition: hasMenuPermission('config:pricetags', context),
      title: 'Etiquetas',
      url: '/pricetags',
      icon: 'tags',
    },
    {
      condition: hasMenuPermission('config:taxes', context),
      title: 'ISP',
      url: '/taxes',
      icon: 'percentage',
    },
    {
      condition: hasMenuPermission('config:prices_combustivel', context),
      title: 'Preços Combustiveis',
      url: '/prices/combustivel',
      icon: 'gas-pump',
    },
    {
      condition: hasMenuPermission('config:nozzles', context),
      title: 'Associar Mangueiras a Produtos',
      url: '/nozzles',
      icon: 'bezier-curve',
    },
    {
      condition: hasMenuPermission('config:veeder_root', context),
      title: 'Veeder-Root',
      url: '/veeder-root',
      icon: 'tachometer-alt',
    },
    {
      condition: hasMenuPermission('config:series', context),
      title: 'Séries',
      url: '/series',
      icon: 'list-ol',
    },
    {
      condition: hasMenuPermission('config:employees', context),
      title: 'Gestão de utilizadores',
      url: '/employees',
      icon: 'users-cog',
    },
    {
      condition: hasMenuPermission('config:categories', context),
      title: 'Gestão de Categorias',
      url: '/categories',
      icon: 'layer-group',
    },
    {
      condition: hasMenuPermission('config:segmentacao', context),
      title: 'Gestão de Segmentações',
      url: '/segmentacao',
      icon: 'user-tag',
    },
    {
      condition: hasMenuPermission('config:zones', context),
      title: 'Gestão de Zonas',
      url: '/zones',
      icon: 'map',
    },
  ];

  return (
    <div className="menu-wrapper">
      {context['demo'] === 'true' || window.location.hostname === 'demo-react.eposgestofinal.com' ? (
        <div className="demo-banner">
          <span>DEMO MODE</span>
          <button type="button" className="demo-exit-button" onClick={exitDemo}>
            Sair
          </button>
        </div>
      ) : null}
      
      <nav className="main-nav">
        <div className="nav-container">
          <div className="nav-brand">
            <button 
              type="button" 
              className={classNames('nav-toggle', { 'is-open': isMenuOpen })}
              onClick={handleMenuToggle}
              aria-label="Toggle navigation"
            >
              <i className={`fas ${isMenuOpen ? 'fa-times' : 'fa-bars'}`}></i>
            </button>
            <Link to="/" className="brand-link" onClick={handleLinkClick}>
            <img src="https://eposgestofinal.com/site/images/epos.png" alt="Logo" style={{ width: '2rem' }} />
              <span>ePOS</span>
            </Link>
          </div>

          <div className={classNames('nav-collapse', { 'show': isMenuOpen })}>
            <ul className="nav-menu">
              {context['memberType'] > 0 && (
                <>
                  {[1,3,4,9,10].includes(context['memberType']) && hasMenuPermission('pos', context) && (
                    <li className="nav-item">
                      <Link to="/pos" className="nav-link" target="_blank" onClick={handleLinkClick}>
                        <i className="fas fa-cash-register"></i>
                        <span>POS</span>
                      </Link>
                    </li>
                  )}

                  {[4].includes(context['memberType']) && hasMenuPermission('tools', context) && (
                    <Submenu
                      title="Ferramentas"
                      icon="tools"
                      width="110px"
                      submenus={submenuToolsLojista}
                      onClose={handleLinkClick}
                    />
                  )}

                  {hasMenuPermission('accounting', context) && (
                    <Submenu
                      title="Contabilidade"
                      icon="calculator"
                      width="110px"
                      submenus={submenucontabilidade}
                      onClose={handleLinkClick}
                    />
                  )}

                  {[1,3,9,10].includes(context['memberType']) && hasMenuPermission('tools', context) && (
                    <Submenu
                      title="Ferramentas"
                      icon="tools"
                      width="110px"
                      submenus={submenuTools}
                      onClose={handleLinkClick}
                    />
                  )}
                  
                  {hasMenuPermission('documents', context) && (
                    <Submenu
                      title="Documentos"
                      icon="file-alt"
                      width="110px"
                      submenus={submenuDocuments}
                      onClose={handleLinkClick}
                    />
                  )}
                  
                  {hasMenuPermission('assistencias', context) && (
                    <li className={classNames('nav-item', { 'active': context['page'] === 'assistencias' })}>
                      <a href="https://eposgestofinal.com/index.php?page=assistencias" className="nav-link" onClick={handleLinkClick}>
                        <i className="fas fa-ticket-alt"></i>
                        <span>Tickets</span>
                      </a>
                    </li>
                  )}
                </>
              )}

              {[1, 9, 10].includes(context['memberType']) && hasMenuPermission('config', context) && (
                <Submenu
                  title="Configuração"
                  icon="cog"
                  width="115px"
                  submenus={configTools}
                  onClose={handleLinkClick}
                />
              )}

              {[10].includes(context['memberType']) && (
                <Submenu
                  title="Administração"
                  icon="user-shield"
                  width="120px"
                  submenus={adminTools}
                  onClose={handleLinkClick}
                />
              )}
            </ul>

            <div className="nav-right">
              <ul className="nav-menu">
                {context['prizes'] === 't' && [1, 3, 10].includes(context['memberType']) && (
                  <li className={classNames('nav-item', { 'active': context['page'] === 'premios' })}>
                    <a href="https://eposgestofinal.com/index.php?page=premios" className="nav-link" onClick={handleLinkClick}>
                      <i className="fas fa-award"></i>
                      <span>Prémios</span>
                    </a>
                  </li>
                )}

                <li className="nav-item alerts-item" ref={alertsDropdownRef}>
                  <button
                    type="button"
                    className={classNames('nav-link alerts-link', { 'has-alerts': activeAlerts.length > 0 })}
                    onClick={handleAlertsClick}
                    style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}
                  >
                    <i className="fas fa-bell"></i>
                    {activeAlerts.length > 0 && (
                      <span className="alerts-badge">{activeAlerts.length}</span>
                    )}
                  </button>
                  {isAlertsDropdownOpen && activeAlerts.length > 0 && (
                    <div className="alerts-dropdown" style={{ display: 'block' }}>
                      <div className="alerts-header">Alertas</div>
                      <div className="alerts-list">
                        {activeAlerts.map((alert, index) => (
                          <a
                            key={index}
                            href={alert.url || '#'}
                            className="alert-item"
                            onClick={(e) => {
                              if (alert.url) {
                                window.location.href = alert.url;
                              }
                              e.preventDefault();
                              setIsAlertsDropdownOpen(false);
                            }}
                          >
                            <div className={classNames('alert-icon', {
                              'alert-error': alert.type === 'error',
                              'alert-warning': alert.type !== 'error'
                            })}>
                              <i className={classNames('fas', {
                                'fa-exclamation-circle': alert.type === 'error',
                                'fa-exclamation-triangle': alert.type !== 'error'
                              })}></i>
                            </div>
                            <div className="alert-content">
                              <div className="alert-message">{alert.message}</div>
                            </div>
                          </a>
                        ))}
                      </div>
                    </div>
                  )}
                </li>

                <li className="nav-item user-profile">
                  {context['pontos'] > 0 && [2].includes(context['memberType']) && (
                    <div className="points-badge">
                      <span className="points-label">Pontos</span>
                      <span className="points-value" id="pontosempresa">{context['pontos']}</span>
                    </div>
                  )}
                  
                  <div className="profile-menu">
                    <img
                      src={'https://eposgestofinal.com/' + (context['avatar'] || (context['idemployee'] ? 'site/images/profile.jpg' : 'site/images/suitcase.png'))}
                      alt={context['name']}
                      className="profile-avatar"
                      onClick={() => window.location.href = 'https://eposgestofinal.com/index.php?page=profile'}
                    />
                    <div className="profile-info">
                      <span className="profile-name">{context['name']}</span>
                      <a href="https://eposgestofinal.com/index.php?logout=1" className="logout-link" onClick={handleLinkClick}>
                        <i className="fas fa-sign-out-alt"></i>
                        <span>Terminar Sessão</span>
                      </a>
                    </div>
                  </div>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </nav>

      {/* Modal de alertas críticos */}
      {showCriticalModal && (
        <div className="critical-alerts-modal">
          <div className="critical-alerts-modal-overlay" onClick={hideCriticalAlertsModal}></div>
          <div className="critical-alerts-modal-content">
            <div className="critical-alerts-modal-header">
              <i className="fas fa-exclamation-triangle"></i>
              <h3>Alertas críticos</h3>
              <button
                type="button"
                className="critical-alerts-modal-close"
                onClick={hideCriticalAlertsModal}
                aria-label="Fechar"
              >
                &times;
              </button>
            </div>
            <div className="critical-alerts-modal-body">
              {criticalAlerts.map((alert, index) => (
                <div key={index} className="critical-alert-item">
                  {alert.url ? (
                    <div className="critical-alert-link-wrapper">
                      <a href={alert.url} className="critical-alert-link" onClick={hideCriticalAlertsModal}>
                        <i className="fas fa-external-link-alt"></i> {alert.message}
                      </a>
                      <div className="critical-alert-link-hint">(Clique para resolver)</div>
                    </div>
                  ) : (
                    <span>{alert.message}</span>
                  )}
                </div>
              ))}
            </div>
            <div className="critical-alerts-modal-footer">
              <button
                type="button"
                className="critical-alerts-modal-btn"
                onClick={hideCriticalAlertsModal}
              >
                Fechar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Menu;